/**
 * Converts Hex to HSL
 */
export const hexToHsl = (hex) => {
  let cleanHex = hex.replace('#', '');
  if (cleanHex.length === 3) {
    cleanHex = cleanHex.split('').map(c => c + c).join('');
  }
  let r = parseInt(cleanHex.substring(0, 2), 16) / 255;
  let g = parseInt(cleanHex.substring(2, 4), 16) / 255;
  let b = parseInt(cleanHex.substring(4, 6), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  let l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      case b: h = (r - g) / d + 4; break;
    }
    h /= 6;
  }

  return [Math.round(h * 360), Math.round(s * 100), Math.round(l * 100)];
};

/**
 * Converts HSL to Hex
 */
export const hslToHex = (h, s, l) => {
  s /= 100;
  l /= 100;
  const a = s * Math.min(l, 1 - l);
  const f = n => {
    const k = (n + h / 30) % 12;
    const color = l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
    return Math.round(255 * color).toString(16).padStart(2, '0');
  };
  return `#${f(0)}${f(8)}${f(4)}`;
};

/**
 * Adjust hex color brightness
 */
export const adjustColorBrightness = (hex, percent) => {
  let num = parseInt(hex.replace('#', ''), 16);
  if (isNaN(num)) return '#0d9488';
  let amt = Math.round(2.55 * percent);
  let R = (num >> 16) + amt;
  let G = (num >> 8 & 0x00FF) + amt;
  let B = (num & 0x0000FF) + amt;
  return `#${(0x1000000 + (R < 255 ? (R < 1 ? 0 : R) : 255) * 0x10000 + (G < 255 ? (G < 1 ? 0 : G) : 255) * 0x100 + (B < 255 ? (B < 1 ? 0 : B) : 255)).toString(16).slice(1)}`;
};

/**
 * Extracts dominant, accent, body text, and header background colors from an image URL / Data URL.
 */
export const extractColorsFromImage = (imageSrc) => {
  return new Promise((resolve) => {
    const fallbackPalette = {
      primary: '#0d9488',
      secondary: '#1d4ed8',
      textColor: '#111827',
      headerBgColor: '#f0fdfa'
    };

    if (!imageSrc) {
      resolve(fallbackPalette);
      return;
    }

    const img = new Image();
    img.crossOrigin = 'Anonymous';
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        const maxDimension = 150;
        let width = img.naturalWidth || img.width;
        let height = img.naturalHeight || img.height;

        if (width > height && width > maxDimension) {
          height = Math.round((height * maxDimension) / width);
          width = maxDimension;
        } else if (height > maxDimension) {
          width = Math.round((width * maxDimension) / height);
          height = maxDimension;
        }

        canvas.width = width;
        canvas.height = height;
        ctx.drawImage(img, 0, 0, width, height);

        const imageData = ctx.getImageData(0, 0, width, height).data;
        const colorCounts = {};
        const darkColorCounts = {};

        for (let i = 0; i < imageData.length; i += 4) {
          const r = imageData[i];
          const g = imageData[i + 1];
          const b = imageData[i + 2];
          const a = imageData[i + 3];

          // Skip transparent or near-transparent pixels
          if (a < 128) continue;

          const brightness = (r * 299 + g * 587 + b * 114) / 1000;

          // Track dark tones (for text font)
          if (brightness >= 10 && brightness <= 50) {
            const qR = Math.round(r / 8) * 8;
            const qG = Math.round(g / 8) * 8;
            const qB = Math.round(b / 8) * 8;
            const darkKey = `${qR},${qG},${qB}`;
            darkColorCounts[darkKey] = (darkColorCounts[darkKey] || 0) + 1;
          }

          // Skip near whites and near blacks for dominant/secondary hue extraction
          if (brightness > 240 || brightness < 25) continue;

          // Quantize color to 16-step buckets to group similar hues
          const qR = Math.round(r / 16) * 16;
          const qG = Math.round(g / 16) * 16;
          const qB = Math.round(b / 16) * 16;
          const key = `${qR},${qG},${qB}`;

          colorCounts[key] = (colorCounts[key] || 0) + 1;
        }

        const sortedColors = Object.entries(colorCounts)
          .sort((a, b) => b[1] - a[1])
          .map(([rgbStr]) => {
            const [r, g, b] = rgbStr.split(',').map(Number);
            const toHex = (c) => Math.min(255, Math.max(0, c)).toString(16).padStart(2, '0');
            return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
          });

        if (sortedColors.length === 0) {
          resolve(fallbackPalette);
          return;
        }

        const primary = sortedColors[0];
        const [primH, primS] = hexToHsl(primary);

        // 1. Pick secondary accent color (prefer distinct hue with good contrast)
        let secondary = sortedColors.find(c => {
          const [h, s, l] = hexToHsl(c);
          const hueDiff = Math.abs(h - primH);
          return (hueDiff > 30 && hueDiff < 330) && s > 25 && l > 20 && l < 80;
        }) || sortedColors[1] || hslToHex((primH + 35) % 360, Math.min(primS, 75), 40);

        // 2. Determine harmonious dark text color
        let textColor = '#111827';
        const sortedDark = Object.entries(darkColorCounts).sort((a, b) => b[1] - a[1]);
        if (sortedDark.length > 0) {
          const [r, g, b] = sortedDark[0][0].split(',').map(Number);
          const toHex = (c) => Math.min(255, Math.max(0, c)).toString(16).padStart(2, '0');
          textColor = `#${toHex(r)}${toHex(g)}${toHex(b)}`;
        } else {
          // Deep brand-tinted dark charcoal
          textColor = hslToHex(primH, Math.min(primS, 30), 10);
        }

        // 3. Determine soft, premium header background tint from primary color
        const headerBgColor = hslToHex(primH, Math.min(primS, 55), 97);

        resolve({
          primary,
          secondary,
          textColor,
          headerBgColor
        });
      } catch (err) {
        console.warn('Could not extract colors from canvas:', err);
        resolve(fallbackPalette);
      }
    };

    img.onerror = () => {
      resolve(fallbackPalette);
    };

    img.src = imageSrc;
  });
};
